import './common.css'

import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { addServerErrors } from '@/components/forms/common/FormBase'
import { useModal } from '@/components/modal/Modal'
import { Spinner } from '@/components/spinner'
import { useAppContext } from '@/context/app'
import { InventoryGroupPathLabel, InventoryGroupRow, ParentInventoryGroupSelectPopup, ROOT_INVENTORY_GROUP_ID } from '../inventory_groups/common'
import { LocationPathLabel, LocationRow, ParentLocationSelectPopup, ROOT_LOCATION_ID } from '../locations/common'

export interface InventoryItemRow {
  id: string
  groupId?: string | null
  locationId?: string | null
  inventoryNumber?: string | null
  serialNumber?: string | null
  status?: string | null
  createdAt?: string
  updatedAt?: string
}

export interface InventoryItemFormData {
  groupId: string
  locationId: string
  inventoryNumber: string
  serialNumber: string
  status: string
}

export const INVENTORY_ITEM_STATUS_OPTIONS = [
  'active',
  'in_storage',
  'in_service',
  'damaged',
  'disposed',
  'missing',
] as const

export const defaultInventoryItemFormData = (): InventoryItemFormData => ({
  groupId: '',
  locationId: '',
  inventoryNumber: '',
  serialNumber: '',
  status: 'active',
})

const asNullableUUID = (value: string) => {
  if (value === '') {
    return null
  }
  return value
}

const asNullableTrimmed = (value: string) => {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

export const normalizeInventoryItemPayload = (data: InventoryItemFormData) => {
  return {
    groupId: asNullableUUID(data.groupId),
    locationId: asNullableUUID(data.locationId),
    inventoryNumber: asNullableTrimmed(data.inventoryNumber),
    serialNumber: asNullableTrimmed(data.serialNumber),
    status: data.status || 'active',
  }
}

export const InventoryItemRemovePopup = ({
  onClose,
  item,
  onRemoved,
}: {
  onClose: () => void
  item?: InventoryItemRow
  onRemoved: () => void
}) => {
  const { services, translates } = useAppContext()
  const [error, setError] = useState<string | null>(null)

  if (!item || !translates) {
    return null
  }

  const remove = async () => {
    setError(null)
    const response = await services.session.fetchWithToken(`/api/communal/remanent/inventory-items/byid/${item.id}/delete`, {
      method: 'DELETE',
    })
    if (response.status !== 200) {
      const message = await response.text()
      setError(message || translates.for('elements.table.inventory_items.remove_error'))
      return
    }
    onRemoved()
    onClose()
  }

  return <div className='dialog'>
    <div className='dialog-text'>
      {translates.for('elements.table.inventory_items.remove_ask').replace('{name}', item.inventoryNumber || item.serialNumber || item.id)}
    </div>
    <div className='dialog-btns'>
      <div className='dialog-btn' onClick={() => onClose()}>
        <i className='svgfont-no' />
        {translates.for('elements.dialog.yesno.no')}
      </div>
      <div className='dialog-btn' onClick={remove}>
        <i className='svgfont-yes' />
        {translates.for('elements.dialog.yesno.yes')}
      </div>
      {error ? <div className='dialog-error'>{error}</div> : null}
    </div>
  </div>
}

export const InventoryItemEditPopup = ({
  onClose,
  inventoryItemId,
  onSaved,
}: {
  onClose: () => void
  inventoryItemId?: string
  onSaved: (inventoryItemId?: string) => void
}) => {
  const { services, session, translates } = useAppContext()
  const { openModal } = useModal()
  const form = useForm<InventoryItemFormData>({
    mode: 'onChange',
    defaultValues: defaultInventoryItemFormData(),
  })
  const formValues = form.watch()
  const [loading, setLoading] = useState<boolean>(!!inventoryItemId)
  const [groups, setGroups] = useState<InventoryGroupRow[]>([])
  const [locations, setLocations] = useState<LocationRow[]>([])
  const [submitErrors, setSubmitErrors] = useState<string[]>([])

  useEffect(() => {
    services.session.fetchWithToken('/api/communal/remanent/inventory-groups').then(async (response) => {
      if (response.status !== 200) {
        setGroups([])
        return
      }
      const data = await response.json()
      setGroups(Array.isArray(data) ? data : [])
    })

    services.session.fetchWithToken('/api/communal/remanent/locations').then(async (response) => {
      if (response.status !== 200) {
        setLocations([])
        return
      }
      const data = await response.json()
      setLocations(Array.isArray(data) ? data : [])
    })
  }, [])

  useEffect(() => {
    if (inventoryItemId === undefined) {
      form.reset(defaultInventoryItemFormData())
      setLoading(false)
      return
    }

    setLoading(true)
    services.session.fetchWithToken(`/api/communal/remanent/inventory-items/byid/${inventoryItemId}`)
      .then(async (response) => {
        if (response.status !== 200) {
          throw new Error(await response.text())
        }
        return response.json()
      })
      .then((data: InventoryItemRow) => {
        form.reset({
          groupId: data.groupId == null ? '' : String(data.groupId),
          locationId: data.locationId == null ? '' : String(data.locationId),
          inventoryNumber: data.inventoryNumber || '',
          serialNumber: data.serialNumber || '',
          status: data.status || 'active',
        })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [inventoryItemId])

  useEffect(() => {
    if (submitErrors.length === 0 && Object.keys(form.formState.errors).length === 0) {
      return
    }
    setSubmitErrors([])
    form.clearErrors()
  }, [
    formValues.groupId,
    formValues.locationId,
    formValues.inventoryNumber,
    formValues.serialNumber,
    formValues.status,
  ])

  const handle = async (data: InventoryItemFormData) => {
    setSubmitErrors([])
    const payload = normalizeInventoryItemPayload(data)
    const url = inventoryItemId === undefined
      ? '/api/communal/remanent/inventory-items'
      : `/api/communal/remanent/inventory-items/byid/${inventoryItemId}/update`
    const method = inventoryItemId === undefined ? 'POST' : 'PUT'

    const response = await services.session.fetchWithToken(url, {
      method,
      body: JSON.stringify(payload),
    })

    if (response.status !== 200) {
      const errors = await response.json().catch(() => null)
      if (errors) {
        addServerErrors(form, session.translates, errors)
        const translatedErrors = Object.values(errors as Record<string, { message?: string }>).map((error) => {
          if (!error?.message) {
            return translates.for('errors.general_error')
          }
          return translates.for(error.message)
        })
        setSubmitErrors(translatedErrors)
      } else {
        setSubmitErrors([translates.for('errors.general_error')])
      }
      return
    }

    const result = inventoryItemId === undefined
      ? await response.json().catch(() => null)
      : null

    onSaved(result?.id)
    onClose()
  }

  if (!session || !translates || loading) {
    return <Spinner />
  }

  const selectedGroupId = form.watch('groupId') === '' ? null : form.watch('groupId')
  const selectedLocationId = form.watch('locationId') === '' ? null : form.watch('locationId')
  const selectedGroup = selectedGroupId == null ? null : groups.find((group) => group.id === selectedGroupId) || null
  const selectedLocation = selectedLocationId == null ? null : locations.find((location) => location.id === selectedLocationId) || null

  return <form className='main-form' onSubmit={form.handleSubmit(handle)}>
    <div className='form-field'>
      <label>{translates.for('elements.table.inventory_items.group')}</label>
      <InventoryGroupPathLabel groups={groups} selectedId={selectedGroupId} onClick={(row) => {
        // if (!row) {
        //   form.setValue('groupId', '', {
        //     shouldDirty: true,
        //     shouldValidate: true,
        //   })
        //   return
        // }
        if (!row || selectedGroup?.id === row.id) {
          return openModal((props) => <ParentInventoryGroupSelectPopup
            {...props}
            groups={groups}
            selectedId={row?.id}
            onApprove={(value) => {
              form.setValue('groupId', value == null ? '' : String(value), {
                shouldDirty: true,
                shouldValidate: true,
              })
            }}
          />)
        }
        form.setValue('groupId', String(row.id), {
          shouldDirty: true,
          shouldValidate: true,
        })
      }} />
    </div>

    <div className='form-field'>
      <label>{translates.for('elements.table.inventory_items.location')}</label>
      <LocationPathLabel locations={locations} selectedId={selectedLocationId} onClick={(row) => {
        // if (!row) {
        //   form.setValue('locationId', '', {
        //     shouldDirty: true,
        //     shouldValidate: true,
        //   })
        //   return
        // }
        if (!row || selectedLocation?.id === row.id) {
          return openModal((props) => <ParentLocationSelectPopup
            {...props}
            locations={locations}
            selectedId={row?.id}
            onApprove={(value) => {
              form.setValue('locationId', value == null ? '' : String(value), {
                shouldDirty: true,
                shouldValidate: true,
              })
            }}
          />)
        }
        form.setValue('locationId', String(row.id), {
          shouldDirty: true,
          shouldValidate: true,
        })
      }} />
    </div>

    <div className='form-field'>
      <label>{translates.for('elements.table.inventory_items.inventory_number')}</label>
      <input type='text' {...form.register('inventoryNumber')} />
    </div>

    <div className='form-field'>
      <label>{translates.for('elements.table.inventory_items.serial_number')}</label>
      <input type='text' {...form.register('serialNumber')} />
    </div>

    <div className='form-field'>
      <label>{translates.for('elements.table.inventory_items.status')}</label>
      <select {...form.register('status', { required: true })}>
        {INVENTORY_ITEM_STATUS_OPTIONS.map((status) => (
          <option key={status} value={status}>
            {translates.for(`elements.table.inventory_items.statuses.${status}`)}
          </option>
        ))}
      </select>
    </div>

    <div className='form-buttons'>
      <button className='submit-button' type='submit'>
        {inventoryItemId === undefined
          ? translates.for('elements.table.inventory_items.add')
          : translates.for('elements.table.inventory_items.save')}
      </button>
    </div>
    {submitErrors.length > 0 ? <div className='critic-error-box'>
      {submitErrors.map((error, index) => (
        <div key={error + index}>{error}</div>
      ))}
    </div> : null}
  </form>
}
